import { EASING_FN } from '@/etc/consts'
import { makeBeatCode } from '@/etc/beatcode'

/**
 * Convert a unit/value pair to seconds
 * @param {string} unit - 'beats' | 'frames' | 'seconds'
 * @param {number} val - value
 * @param {Object} settings - project settings
 * @returns {number} seconds
 */
export const secFromUnit = (unit, val, settings) => {
  if (!Number.isFinite(val)) return 0
  if (unit === 'seconds' || !unit) return val
  if (unit === 'frames') return val / (settings.fps || 30)
  if (unit === 'beats') {
    try {
      const bc = makeBeatCode(settings)
      return val * bc.secondsPerQuarter()
    } catch (e) {
      return val * (60 / (settings.bpm || 120))
    }
  }
  return val
}

/**
 * Interpolate between two numeric values
 * @param {number} a - start value
 * @param {number} b - end value
 * @param {number} t - normalized time (0-1)
 * @param {string} easingKey - easing function name
 * @returns {number} interpolated value
 */
export const interp = (a, b, t, easingKey) => {
  const k = easingKey || 'Linear.None'
  try {
    const easeFn = EASING_FN[k] || EASING_FN['Linear.None']
    const tt = typeof easeFn === 'function' ? easeFn(t) : t
    return a + (b - a) * tt
  } catch (e) {
    return a + (b - a) * t
  }
}

/**
 * Evaluate a Smart Key envelope group at a given time
 * @param {Object} group - The smart key group config
 * @param {Object} ctx - Evaluation context
 * @param {number} ctx.localTime - Time relative to strip start (seconds)
 * @param {number} ctx.globalTime - Absolute timeline time (seconds)
 * @param {number} ctx.stripStart - Strip start time (seconds)
 * @param {number} ctx.stripLength - Strip length (seconds)
 * @param {Object} ctx.settings - Project settings (fps, bpm, etc)
 * @param {string} [ctx.scope='global'] - Beat lock scope ('global' | 'local')
 * @returns {number} The resolved numeric value
 */
export const evaluateSmartEnvelope = (group, ctx) => {
  const { localTime, globalTime, stripStart, stripLength, settings } = ctx
  const scope = ctx.scope || 'global'

  // Extract group config with defaults
  const entryOff = secFromUnit(group.offsets?.entry?.unit, group.offsets?.entry?.value || group.offsets?.entry || 0, settings)
  const exitOff = secFromUnit(group.offsets?.exit?.unit, group.offsets?.exit?.value || group.offsets?.exit || 0, settings)
  
  // Outer envelope bounds (timeline domain)
  const entryTime = entryOff
  const exitTime = Math.max(0, stripLength - exitOff)
  
  // Values
  const startVal = Number(group.start?.value) || 0
  const leftVal = Number(group.left?.value) || 0
  const rightVal = Number(group.right?.value) || 0
  const endVal = Number(group.end?.value) || 0

  // 1. Evaluate base envelope (Entry -> Sustain -> Exit)
  let base = 0
  
  // Current region name (for optional per-region micros)
  const currentRegion = localTime <= entryTime
    ? 'entry'
    : localTime >= exitTime
      ? 'exit'
      : 'sustain'

  // Entry phase
  if (localTime <= entryTime) {
    const tpos = entryTime > 0 ? (localTime / entryTime) : 1
    const eKeyEntry = group.entryEasing || 'Linear.None'
    base = interp(startVal, leftVal, tpos, eKeyEntry)
  }
  // Exit phase
  else if (localTime >= exitTime) {
    const span = Math.max(0.0001, stripLength)
    const tpos = Math.min(1, (localTime - exitTime) / (span - exitTime))
    const eKeyExit = group.exitEasing || 'Linear.None'
    base = interp(rightVal, endVal, tpos, eKeyExit)
  }
  // Sustain phase
  else {
    const span = Math.max(0.0001, (exitTime - entryTime))
    const tpos = Math.min(1, Math.max(0, (localTime - entryTime) / span))
    const easingKey = group.sustainEasing || 'Linear.None'
    base = interp(leftVal, rightVal, tpos, easingKey)
  }

  // 2. Evaluate Micro/Impact loop (if present)
  const microByRegion = (group && group.microByRegion && typeof group.microByRegion === 'object')
    ? group.microByRegion
    : null
  const micro = microByRegion
    ? (microByRegion[currentRegion] || null)
    : group.micro
  if (micro) {
    const beatLocked = !!micro.beatLocked
    const effScope = micro.beatLockScope || scope
    const region = microByRegion ? currentRegion : (micro.region || 'sustain')
    const phaseBeats = Number.isFinite(Number(micro.phaseBeats)) ? Number(micro.phaseBeats) : 0
    let phaseSec = 0
    if (phaseBeats !== 0) {
      try {
        const bc = makeBeatCode(settings)
        phaseSec = bc.toSecondsFromBeats(phaseBeats)
      } catch (e) {
        phaseSec = phaseBeats * (60 / (settings.bpm || 120))
      }
    }
    
    // Determine modulation time base and region bounds
    let baseTime = localTime
    let regionStart = entryTime
    let regionEnd = exitTime

    if (beatLocked) {
      if (effScope === 'global') {
        baseTime = globalTime
        const gStart = stripStart
        const gEntryEnd = gStart + entryOff
        const gExitStart = gStart + stripLength - exitOff
        const gEnd = gStart + stripLength
        regionStart = region === 'entry' ? gStart : (region === 'exit' ? gExitStart : gEntryEnd)
        regionEnd = region === 'entry' ? gEntryEnd : (region === 'exit' ? gEnd : gExitStart)
      } else {
        // beat-locked but local scope: effectively beat-grid starting at 0
        baseTime = localTime
        regionStart = region === 'entry' ? 0 : (region === 'exit' ? exitTime : entryTime)
        regionEnd = region === 'entry' ? entryTime : (region === 'exit' ? stripLength : exitTime)
      }
    } else {
      // time-locked local scope
      baseTime = localTime
      regionStart = region === 'entry' ? 0 : (region === 'exit' ? exitTime : entryTime)
      regionEnd = region === 'entry' ? entryTime : (region === 'exit' ? stripLength : exitTime)
    }

    // Only apply if within region bounds
    const gateTime = baseTime
    // Phase is cyclic (should not change whether we are inside region); apply to sampling time only.
    // Positive phaseBeats means "later" (delay), so we subtract seconds here.
    const phaseTime = baseTime - phaseSec

    if (gateTime >= regionStart && gateTime <= regionEnd) {
      const D = Math.max(0, regionEnd - regionStart)
      const useInstances = !!micro.instancesEnabled
      const n = Math.max(1, Math.floor(Number.isFinite(micro.instances) ? micro.instances : 1))
      const duty = Math.max(0, Math.min(1, Number.isFinite(micro.duty) ? micro.duty : 1))
      const amp = Number.isFinite(micro.amp) ? micro.amp : 0.2
      const shape = micro.shape || 'parent'

      let everySec = secFromUnit(micro.every?.unit, micro.every?.value || 0, settings)
      let spanSec = secFromUnit(micro.span?.unit, micro.span?.value || 0, settings) || everySec

      if (useInstances && D > 0) {
        everySec = D / n
        spanSec = Math.max(1e-6, everySec * duty)
      }

      if (everySec > 0) {
        // Calculate instance/cycle timing
        const startAligned = beatLocked && !useInstances
          ? Math.ceil(regionStart / everySec) * everySec
          : regionStart

        // Use phaseTime for cycle position (can be negative to represent wrap)
        const rel = phaseTime - startAligned
        let idx = Math.floor(rel / everySec)
        
        // Clamp instances
        if (useInstances) {
          idx = Math.max(0, Math.min(n - 1, Math.floor((phaseTime - regionStart) / everySec)))
        }

        // Compute normalized progress within the active instance
        const instanceStart = (useInstances ? regionStart : startAligned) + idx * everySec
        const within = phaseTime - instanceStart

        // Apply modulation if within active duty cycle
        if (within >= 0 && within <= spanSec) {
          if (shape === 'parent') {
            const easingKey = micro.easing || (
              region === 'entry' ? (group.entryEasing || 'Linear.None') :
              region === 'exit' ? (group.exitEasing || 'Linear.None') :
              (group.sustainEasing || 'Linear.None')
            )
            
            const u = Math.min(1, Math.max(0, within / Math.max(1e-6, spanSec)))
            const m = interp(0, 1, u, easingKey) // 0->1 using easing

            // Compute delta for additive composition
            const regionDelta = region === 'entry'
              ? (leftVal - startVal)
              : region === 'exit'
                ? (endVal - rightVal)
                : (rightVal - leftVal)

            base = base + amp * m * regionDelta
          }
          // Window modulation: oscillate directly between low/high bounds (ignores envelope delta).
          // Intended for effect overlay "Dyn" window preview and Beatcode-style modulation.
          if (shape === 'window') {
            const win = micro.window || {}
            const low = Number.isFinite(Number(win.low)) ? Number(win.low) : null
            const high = Number.isFinite(Number(win.high)) ? Number(win.high) : null
            if (Number.isFinite(low) && Number.isFinite(high) && high !== low) {
              const easingKey = micro.easing || (
                region === 'entry' ? (group.entryEasing || 'Linear.None') :
                region === 'exit' ? (group.exitEasing || 'Linear.None') :
                (group.sustainEasing || 'Linear.None')
              )

              const u = Math.min(1, Math.max(0, within / Math.max(1e-6, spanSec)))
              // Default: yoyo within a single cycle (triangle) so it goes up and down at the selected rate.
              const yoyo = (typeof micro.yoyo === 'boolean') ? micro.yoyo : true
              let m = 0
              if (yoyo) {
                if (u <= 0.5) m = interp(0, 1, u * 2, easingKey)
                else m = interp(1, 0, (u - 0.5) * 2, easingKey)
              } else {
                m = interp(0, 1, u, easingKey)
              }

              // Map to [low, high]
              const lo = Math.min(low, high)
              const hi = Math.max(low, high)
              const center = (lo + hi) / 2
              const half = (hi - lo) / 2
              // Apply amp as an extra scaling, but clamp back into the window.
              const scaled = center + (m * 2 - 1) * half * (Number.isFinite(amp) ? amp : 1)
              base = Math.max(lo, Math.min(hi, scaled))
            }
          }

          // Relative Window modulation: oscillate around base value by radius (additive).
          if (shape === 'windowRelative') {
            const radius = Number.isFinite(Number(micro.radius)) ? Number(micro.radius) : 0
            if (Math.abs(radius) > 1e-6) {
              const easingKey = micro.easing || (
                region === 'entry' ? (group.entryEasing || 'Linear.None') :
                region === 'exit' ? (group.exitEasing || 'Linear.None') :
                (group.sustainEasing || 'Linear.None')
              )
              const u = Math.min(1, Math.max(0, within / Math.max(1e-6, spanSec)))
              const yoyo = (typeof micro.yoyo === 'boolean') ? micro.yoyo : true
              let m = 0
              if (yoyo) {
                if (u <= 0.5) m = interp(0, 1, u * 2, easingKey)
                else m = interp(1, 0, (u - 0.5) * 2, easingKey)
              } else {
                m = interp(0, 1, u, easingKey)
              }
              // Oscillate -1..1 scaled by radius * amp
              const offset = (m * 2 - 1) * radius * (Number.isFinite(amp) ? amp : 1)
              base = base + offset
            }
          }
          // Note: 'custom' shape (legacy pattern) omitted for brevity as per Option B scope; 
          // we focus on parent-shape automation which is the modern SmartKeys intent.
        }
      }
    }
  }

  return base
}

